<script setup>
import { ref, computed, watch } from 'vue'
import ModalBox from '@/components/modals/ModalBox.vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import TabGroup from '@/components/Atoms/TabGroup.vue'
import TabItem from '@/components/Atoms/TabItem.vue'
import { VOCAB_MAX, parseDictionaryText, planImport } from './captionRecognition'

const open = defineModel({ type: Boolean, default: false })

const props = defineProps({
	// { name, text } of the chosen file.
	file: { type: Object, default: null },
	// Current dictionary rows (for Merge and duplicate detection).
	existingRows: { type: Array, default: () => [] },
})

const emit = defineEmits(['confirm'])

const PREVIEW_LIMIT = 8

const existingCount = computed(() => props.existingRows.filter((r) => r.term.trim()).length)
const mode = ref('merge')
watch(open, (isOpen) => {
	if (isOpen) mode.value = existingCount.value ? 'merge' : 'replace'
})

const parsed = computed(() => (props.file ? parseDictionaryText(props.file.text, props.file.name) : { entries: [], errors: [] }))
const plan = computed(() => planImport(parsed.value, props.existingRows, mode.value))
const nothingToImport = computed(() => !parsed.value.entries.length && !parsed.value.errors.length)
const confirmLabel = computed(() => {
	const n = plan.value.toAdd.length
	const terms = `${n} ${n === 1 ? 'term' : 'terms'}`
	return mode.value === 'replace' && existingCount.value ? `Replace with ${terms}` : `Add ${terms}`
})

function confirm() {
	if (!plan.value.toAdd.length) return
	emit('confirm', { mode: mode.value, plan: plan.value })
	open.value = false
}
</script>

<template>
	<ModalBox v-model="open" size="lg" title="Import dictionary" scroll-body mobile-placement="bottom" wrapper-classes="lg:max-h-[85vh] flex flex-col" body-classes="px-4 sm:px-6">
		<div class="flex flex-col gap-4 pb-4" data-testid="import-preview">
			<p class="m-0 -mt-2 truncate text-tiny text-surface-7" data-testid="import-filename">{{ file?.name }}</p>

			<div v-if="nothingToImport" class="rounded-lg bg-surface-3/60 px-4 py-6 text-center text-tiny text-surface-8">
				This file has no terms. Put one per line: <code>Term</code> or <code>Term|pron1,pron2</code>.
			</div>

			<template v-else>
				<ul class="m-0 grid list-none grid-cols-3 gap-2 p-0" data-testid="import-counts">
					<li class="flex flex-col rounded-lg bg-surface-3/60 px-3 py-2">
						<span class="text-lg font-semibold tabular-nums text-surface-text-2" data-testid="count-add">{{ plan.toAdd.length }}</span>
						<span class="text-[11px] text-surface-7">to add</span>
					</li>
					<li class="flex flex-col rounded-lg bg-surface-3/60 px-3 py-2">
						<span class="text-lg font-semibold tabular-nums" :class="plan.duplicates ? 'text-gold' : 'text-surface-text-2'" data-testid="count-duplicates">{{ plan.duplicates }}</span>
						<span class="text-[11px] text-surface-7">{{ mode === 'merge' ? 'already in the dictionary or repeated' : 'repeated in the file' }}</span>
					</li>
					<li class="flex flex-col rounded-lg bg-surface-3/60 px-3 py-2">
						<span class="text-lg font-semibold tabular-nums" :class="plan.errors ? 'text-error-light' : 'text-surface-text-2'" data-testid="count-errors">{{ plan.errors }}</span>
						<span class="text-[11px] text-surface-7">{{ plan.errors === 1 ? 'line skipped' : 'lines skipped' }}</span>
					</li>
				</ul>

				<div v-if="existingCount" class="flex flex-col gap-2">
					<span id="import-mode-label" class="text-tiny font-medium text-surface-text-1">Your dictionary has {{ existingCount }} {{ existingCount === 1 ? 'term' : 'terms' }}</span>
					<TabGroup type="segmented" cols="2" aria-labelledby="import-mode-label" data-testid="import-mode">
						<TabItem :is-active="mode === 'merge'" :aria-pressed="mode === 'merge'" @click="mode = 'merge'">Merge</TabItem>
						<TabItem :is-active="mode === 'replace'" :aria-pressed="mode === 'replace'" @click="mode = 'replace'">Replace</TabItem>
					</TabGroup>
					<p class="m-0 text-xs text-surface-7" data-testid="import-mode-copy">
						<template v-if="mode === 'merge'">Keeps your terms and adds the new ones. Terms you already have are skipped.</template>
						<template v-else>Removes your {{ existingCount }} {{ existingCount === 1 ? 'term' : 'terms' }} and uses this file instead. Nothing changes until you save.</template>
					</p>
				</div>

				<p v-if="plan.overCap" class="m-0 rounded-lg bg-gold/10 px-3 py-2 text-xs text-gold" role="status" data-testid="import-cap">
					The dictionary holds up to {{ VOCAB_MAX }} terms. The import stops there: {{ plan.overCap }} {{ plan.overCap === 1 ? 'term' : 'terms' }} won't be added.
				</p>

				<section v-if="parsed.errors.length" class="flex flex-col gap-2" aria-labelledby="import-errors-heading">
					<h4 id="import-errors-heading" class="m-0 text-tiny font-medium text-surface-text-1">
						Lines that will be skipped
					</h4>
					<ol class="m-0 flex max-h-48 list-none flex-col divide-y divide-surface-3 overflow-y-auto rounded-lg bg-surface-3/40 p-0" data-testid="import-errors">
						<li v-for="err in parsed.errors" :key="err.line" class="flex flex-col gap-0.5 px-3 py-2 sm:flex-row sm:items-baseline sm:gap-3" data-testid="import-error">
							<span class="shrink-0 text-[11px] font-medium tabular-nums text-surface-7">Line {{ err.line }}</span>
							<span class="min-w-0 truncate font-mono text-xs text-surface-text-1" :title="err.text">{{ err.text.trim() }}</span>
							<span class="text-xs text-error-light sm:ml-auto sm:shrink-0">{{ err.reason }}</span>
						</li>
					</ol>
				</section>

				<section v-if="plan.toAdd.length" class="flex flex-col gap-2" aria-labelledby="import-preview-heading">
					<h4 id="import-preview-heading" class="m-0 text-tiny font-medium text-surface-text-1">To add</h4>
					<ul class="m-0 flex list-none flex-wrap gap-1.5 p-0" data-testid="import-to-add">
						<li v-for="entry in plan.toAdd.slice(0, PREVIEW_LIMIT)" :key="entry.line" class="inline-flex h-7 max-w-full items-center gap-1 rounded-full bg-surface-3 px-3 text-xs text-surface-text-2">
							<span class="truncate">{{ entry.term }}</span>
							<span v-if="entry.soundsLike.length" class="truncate text-surface-7">· {{ entry.soundsLike.join(', ') }}</span>
						</li>
						<li v-if="plan.toAdd.length > PREVIEW_LIMIT" class="inline-flex h-7 items-center px-2 text-xs text-surface-7">+{{ plan.toAdd.length - PREVIEW_LIMIT }} more</li>
					</ul>
				</section>
			</template>
		</div>

		<template #footer>
			<div class="flex flex-col-reverse gap-2 border-t border-surface-3 p-4 sm:flex-row sm:items-center sm:justify-end sm:px-6">
				<FormButton type="secondary" size="md" class="justify-center" @click="open = false">Cancel</FormButton>
				<FormButton type="main" size="md" class="justify-center" :disabled="!plan.toAdd.length" data-testid="import-confirm" @click="confirm">
					{{ plan.toAdd.length ? confirmLabel : 'Nothing to add' }}
				</FormButton>
			</div>
		</template>
	</ModalBox>
</template>
